import React, {MouseEventHandler, useEffect, useRef, useState} from 'react';
import {DesignerSettings, AssetService} from '@taopix/taopix-core/dist/types/types';
import {Theme, ThemeName} from '@taopix/taopix-design-system';
import APIServer from '@taopix/taopix-core/dist/types/Service/APIServer';
import classNames from 'classnames';
import ButtonFactory from './Buttons/ButtonFactory';
import {Common} from '@taopix/taopix-core';

export type AuthenticationViewProps = {
  isOpen: boolean;
  closeEvent: MouseEventHandler;
  assetService: AssetService;
  onAuthenticate: Function;
  designerSettings: DesignerSettings;
  server: APIServer;
  storePollingInterval: (pollRef: NodeJS.Timeout) => void;
};

export type AuthResponse = {
  data: {
    url: string;
  };
  error: AuthError;
};

export type TokenResponse = {
  token: string;
  error: AuthError;
};

export type AuthError = {
  code: number;
  fullMessage: string;
  phrase: string;
};

const ServiceAuth = ({
  isOpen,
  closeEvent,
  assetService,
  onAuthenticate,
  designerSettings,
  server,
  storePollingInterval,
  ...props
}: AuthenticationViewProps) => {
  const [errorData, setErrorData] = useState<AuthError>({} as AuthError);
  const [authWindowHandle, setAuthWindowHandle] = useState<any>(null);
  const [pending, setPending] = useState<boolean>(false);

  useEffect(() => {
    window.addEventListener(
      'message',
      e => {
        if (authWindowHandle && e.data) {
          if (typeof e.data === 'string') {
            const data = JSON.parse(e.data);
            if (data['token'] !== '') {
              onAuthenticate(true, data['token']);
              clearInterval(intervalRef.current);
            } else {
              setErrorData(data['error']);
            }
            authWindowHandle.postMessage('CLOSE', '*');
          }
        }
      },
      false
    );
  });

  // use a ref to hold our timeout
  const intervalRef = useRef<NodeJS.Timeout>();

  //TODO
  const onAuthError = (response: any) => {
    setPending(false);
    setErrorData(response.error);
  };

  const openAuthWindow = (payload: AuthResponse, popupWindow: Window | null) => {
    setPending(false);

    if (payload.data.url) {
      if (popupWindow) {
        popupWindow.window.location.href = payload.data.url;
      }
    } else {
      setErrorData(payload.error);
    }
  };

  const onAuth = () => {
    setPending(true);
    const target = 'AuthenticationWindowOpener_' + assetService.code;

    //if we already have a window open don't open another
    let open = !authWindowHandle;
    if (
      authWindowHandle === null ||
      !authWindowHandle.self ||
      authWindowHandle.self === null ||
      authWindowHandle.closed
    ) {
      open = true;
    }

    if (open) {
      const popupWindow: Window | null = window.open(
        'about:blank',
        target,
        'toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=650,height=750'
      );

      setAuthWindowHandle(popupWindow);

      server
        .post<AuthResponse>(
          '/api/assetservice/authenticate',
          {
            assetservice: JSON.stringify(assetService),
            groupCode: designerSettings.keySettings.groupCode,
          },
          {token: 'token'}
        )
        .then((response: {data: AuthResponse}) => {
          openAuthWindow(response.data, popupWindow);
          startPoll(popupWindow);
        })
        .catch(error => {
          onAuthError(error);
        });
    } else {
      authWindowHandle.focus();
    }
  };

  const startPoll = (popupWindow: Window | null) => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      pollForToken(popupWindow);
    }, 3000);
    storePollingInterval(intervalRef.current);
  };

  const pollForToken = (popupWindow: Window | null) => {
    server
      .post<AuthResponse>(
        '/api/assetservice/' + assetService.code.replaceAll('%', '') + '/accessToken',
        {
          assetservice: JSON.stringify(assetService),
          groupCode: designerSettings.keySettings.groupCode,
        },
        {token: 'token'}
      )
      .then((response: {data: any}) => {
        if (response.data.token !== '') {
          onAuthenticate(true, response.data.token);
          popupWindow?.postMessage('CLOSE', '*');
          authWindowHandle?.postMessage('CLOSE', '*');
          clearInterval(intervalRef.current);
        }
      })
      .catch(error => {
        console.error(error);
      });
  };

  const Error = ({error}: {error: AuthError}) => {
    return (
      <Theme name={ThemeName.Warning} className="px-sm py-xs mt-sm">
        {error.fullMessage}
      </Theme>
    );
  };

  return (
    <div className={classNames(['flex', 'flex-col', 'space-y-md', 'm-md', 'items-center'])}>
      {errorData?.code && <Error error={errorData} />}
      <ButtonFactory
        code={assetService.code}
        onClick={onAuth}
        pending={pending}
        privacyPolicyUrl={Common.getCurrentLocaleString(assetService.privacypolicyurl, true)}
      />
    </div>
  );
};

export default ServiceAuth;
